type MarketingPage = {
  path: string;
  label: string;
  badge: 'LIVE' | 'WORKING MVP' | 'IN ACTIVE BUILD' | 'ROADMAP';
  title: string;
  summary: string;
  sections: Array<{ heading: string; body: string; points: string[] }>;
};

import { MARKETING_SCREENSHOT_ASSETS, MARKETING_VIDEO_ASSET } from './media-manifest';

const MARKETING_THEME_KEY = 'imm_marketing_theme';

const PAGES: MarketingPage[] = [
  {
    path: '/home',
    label: 'Home',
    badge: 'LIVE',
    title: 'Market intelligence for India, with terminal discipline and AI speed.',
    summary: 'IndiEye brings live Indian market data, ML strategy signals, LLM-curated intelligence, and backtesting into one professional operating surface.',
    sections: [
      {
        heading: 'Product Proof',
        body: 'Built as an operator-first system for NSE, BSE, MCX, and India macro context.',
        points: ['10 Product Pages', '31 API Routes', '100+ Data Inputs', '8 Language Support'],
      },
      {
        heading: 'Core Engines',
        body: 'Three integrated intelligence layers keep signal quality high and explainability explicit.',
        points: ['ML Strategy Engine', 'LLM Intelligence Pipeline', 'Backtesting and Accountability Layer'],
      },
    ],
  },
  {
    path: '/platform',
    label: 'Platform',
    badge: 'WORKING MVP',
    title: 'One operating system for Indian market intelligence.',
    summary: 'A canvas-first terminal architecture with stream resilience, feature flags, and production-safe rollout controls.',
    sections: [
      {
        heading: 'Architecture',
        body: 'Frontend charts, API fan-out, and inference surfaces compose a resilient runtime stack.',
        points: ['Bloomberg-style cockpit shell', 'Edge API routes and cache telemetry', 'Desktop + Web + PWA support'],
      },
      {
        heading: 'Data Layer',
        body: 'Indices, options, breadth, commodities, FX, and macro context are normalized into one grammar.',
        points: ['NSE + Upstox integrations', 'FII/DII and market radar pack', 'Strict live-data policy controls'],
      },
    ],
  },
  {
    path: '/intelligence',
    label: 'Intelligence',
    badge: 'LIVE',
    title: 'LLM intelligence that prioritizes relevance, not noise.',
    summary: 'Morning briefs, priority-ranked headlines, entity extraction, and why-it-matters framing for India-first decision loops.',
    sections: [
      {
        heading: 'Pipeline',
        body: 'Classification and extraction chain produces explainable context for each headline.',
        points: ['Impact and category scoring', 'Sector and company entity tagging', 'Why-it-matters one-line synthesis'],
      },
      {
        heading: 'Output Layer',
        body: 'Users get multi-turn chat plus intelligence cards with context-aware suggestions.',
        points: ['SSE streaming chat responses', 'Rich cards: stock, sector, earnings, chart', 'Feedback loop and telemetry hooks'],
      },
    ],
  },
  {
    path: '/strategy-lab',
    label: 'Strategy Lab',
    badge: 'WORKING MVP',
    title: 'ML strategy guidance with explicit risk framing.',
    summary: 'Regime detection and confidence-aware recommendation flow designed for transparent, reviewable decisions.',
    sections: [
      {
        heading: 'Model Stack',
        body: 'Signals are generated from blended market features and calibrated confidence envelopes.',
        points: ['Regime and momentum classifiers', 'Feature vectors across breadth and derivatives', 'Sector-sensitive recommendations'],
      },
      {
        heading: 'Decision Surface',
        body: 'Every recommendation includes context, risk level, and monitor points.',
        points: ['Entry/exit structure', 'Risk overlays and caution states', 'Audit-friendly signal summaries'],
      },
    ],
  },
  {
    path: '/markets-map',
    label: 'Markets & India Map',
    badge: 'IN ACTIVE BUILD',
    title: 'Live markets plus India-focused geographic intelligence.',
    summary: 'Indices, options, sector breadth, and infrastructure-linked India map narratives in one flow.',
    sections: [
      {
        heading: 'Markets Layer',
        body: 'Real-time panels cover index movement, options microstructure, and liquidity warnings.',
        points: ['Options OI and depth signals', 'Heatmap standards with legend encoding', 'Global telemetry footer diagnostics'],
      },
      {
        heading: 'India Map Layer',
        body: 'State overlays and macro narratives connect local developments to market behavior.',
        points: ['State-level context slots', 'Globe-to-map transition narrative', 'India-first economic framing'],
      },
    ],
  },
  {
    path: '/backtesting-portfolio',
    label: 'Backtesting & Portfolio',
    badge: 'WORKING MVP',
    title: 'From signal to accountability.',
    summary: 'Backtest evidence, portfolio context, and broker-linked execution readiness in a single review loop.',
    sections: [
      {
        heading: 'Backtest Studio',
        body: 'Historical simulation outputs with strategy-level performance narratives.',
        points: ['Equity curve and drawdown views', 'Monthly regime snapshots', 'Benchmark-aware evaluation'],
      },
      {
        heading: 'Portfolio Context',
        body: 'Strategy output and real holdings context are aligned for decision follow-through.',
        points: ['Portfolio exposure summaries', 'Risk and allocation checks', 'Broker connectivity ready'],
      },
    ],
  },
  {
    path: '/mvp-demo',
    label: 'MVP Demo',
    badge: 'IN ACTIVE BUILD',
    title: 'Working product walkthrough and media gallery.',
    summary: 'A chaptered demo flow and gallery slots designed for real captures from production-like runs.',
    sections: [
      {
        heading: 'Demo Narrative',
        body: 'Walk through dashboard, strategy, intelligence, markets, options, and monitoring workflows.',
        points: ['Hero walkthrough player slot', 'Chapter jump points', 'Product state labels per module'],
      },
      {
        heading: 'Media Gallery',
        body: 'Gallery framework is wired for real screenshots and video captures as they are finalized.',
        points: ['10-screen gallery slots', 'Version-tagged captures', 'Asset pipeline ready'],
      },
    ],
  },
  {
    path: '/founders',
    label: 'Founders',
    badge: 'ROADMAP',
    title: 'India-first market infrastructure, built by operators.',
    summary: 'Leadership, mission, and product philosophy presented with verification-first trust language.',
    sections: [
      {
        heading: 'Leadership',
        body: 'ADITYA (CTO, CEO) and PRITAM (CEO) are shaping the long-horizon product direction.',
        points: ['Mission and operating principles', 'Why India, why now', 'Direct collaboration channels'],
      },
      {
        heading: 'Trust Layer',
        body: 'Transparent roadmap and explicit product-state labeling maintain credibility during active build.',
        points: ['No hype policy', 'Live versus in-build separation', 'Roadmap clarity'],
      },
    ],
  },
];

function getPage(path: string): MarketingPage {
  const normalized = path === '/' ? '/home' : path;
  return PAGES.find((page) => page.path === normalized) || PAGES[0];
}

function getBadgeClass(badge: MarketingPage['badge']): string {
  if (badge === 'LIVE') return 'mk-badge mk-badge--live';
  if (badge === 'WORKING MVP') return 'mk-badge mk-badge--mvp';
  if (badge === 'IN ACTIVE BUILD') return 'mk-badge mk-badge--build';
  return 'mk-badge mk-badge--roadmap';
}

function getMarketTapeHtml(): string {
  return [
    'NIFTY 50 +0.82%',
    'BANK NIFTY +1.10%',
    'USDINR 83.20',
    'CRUDE -0.65%',
    'FII +2300Cr',
    'VIX 13.5',
  ].map((item) => `<span>${item}</span>`).join('');
}

function heroGlobe(): string {
  return `
    <div class="mk-globe" id="mk-globe" aria-label="Interactive India-focused globe">
      <div class="mk-globe__sphere"></div>
      <div class="mk-globe__halo"></div>
      <div class="mk-globe__arc mk-globe__arc--1"></div>
      <div class="mk-globe__arc mk-globe__arc--2"></div>
      <div class="mk-globe__node mk-globe__node--mumbai">Mumbai</div>
      <div class="mk-globe__node mk-globe__node--delhi">Delhi</div>
      <div class="mk-globe__node mk-globe__node--bengaluru">Bengaluru</div>
      <div class="mk-globe__node mk-globe__node--hyderabad">Hyderabad</div>
      <div class="mk-globe__node mk-globe__node--chennai">Chennai</div>
      <div class="mk-globe__node mk-globe__node--ahmedabad">Ahmedabad</div>
    </div>
  `;
}

function renderHero(): string {
  return `
    <section class="mk-hero">
      <div class="mk-hero__content">
        <p class="mk-kicker">THE BLOOMBERG-STYLE MARKET INTELLIGENCE LAYER FOR INDIA</p>
        <h1>Market intelligence for India, built with terminal discipline and AI speed.</h1>
        <p>IndiEye combines live market data, strategy signals, intelligence analysis, and portfolio accountability in one professional operating surface.</p>
        <div class="mk-hero__actions">
          <a href="#/platform" class="mk-btn mk-btn--primary">Explore Platform</a>
          <a href="#/mvp-demo" class="mk-btn mk-btn--ghost">Watch MVP Demo</a>
        </div>
        <div class="mk-proof-grid">
          <div><strong>10</strong><span>Product Pages</span></div>
          <div><strong>31</strong><span>API Routes</span></div>
          <div><strong>100+</strong><span>News & Data Inputs</span></div>
          <div><strong>8</strong><span>Indian Languages</span></div>
          <div><strong>ML + LLM</strong><span>Intelligence Stack</span></div>
          <div><strong>NSE / BSE / MCX</strong><span>India-first Market Focus</span></div>
        </div>
      </div>
      <div class="mk-hero__visual">
        ${heroGlobe()}
      </div>
    </section>
  `;
}

function renderMediaBlock(): string {
  const video = MARKETING_VIDEO_ASSET;
  const hasVideo = Boolean(video.src);
  const hasPoster = Boolean(video.poster);
  const gallery = MARKETING_SCREENSHOT_ASSETS.slice(0, 10).map((asset) => {
    if (asset.src) {
      return `<figure><img src="${asset.src}" alt="${asset.label} screenshot" loading="lazy" /><figcaption>${asset.label}</figcaption></figure>`;
    }
    return `<div>${asset.label}</div>`;
  }).join('');

  return `
    <section class="mk-media">
      <article class="mk-panel">
        <header>
          <p class="mk-kicker">MVP DEMO</p>
          <h3>Walkthrough Player</h3>
        </header>
        ${hasVideo
          ? `<video class="mk-video" controls preload="metadata"><source src="${video.src}" />Your browser does not support video playback.</video>`
          : hasPoster
            ? `<figure class="mk-video-poster-wrap"><img class="mk-video-poster" src="${video.poster}" alt="${video.title} poster" loading="lazy" /><figcaption>Poster ready. Replace with final MVP video file in manifest when available.</figcaption></figure>`
            : `<div class="mk-video-placeholder" role="img" aria-label="MVP video placeholder"><span>Demo video slot ready for final cut</span></div>`}
      </article>
      <article class="mk-panel">
        <header>
          <p class="mk-kicker">PRODUCT GALLERY</p>
          <h3>Terminal Screen Series</h3>
        </header>
        <div class="mk-gallery-grid">${gallery}</div>
      </article>
    </section>
  `;
}

function renderPage(page: MarketingPage): string {
  const nav = PAGES.map((item) => {
    const active = item.path === page.path ? 'mk-nav__link--active' : '';
    return `<a href="#${item.path}" class="mk-nav__link ${active}">${item.label}</a>`;
  }).join('');

  const sectionBlocks = page.sections.map((section) => `
    <article class="mk-panel">
      <header>
        <p class="mk-kicker">${section.heading.toUpperCase()}</p>
        <h3>${section.heading}</h3>
      </header>
      <p>${section.body}</p>
      <ul>
        ${section.points.map((point) => `<li>${point}</li>`).join('')}
      </ul>
    </article>
  `).join('');

  return `
    <div class="mk-site" data-theme="${getMarketingTheme()}">
      <header class="mk-header">
        <a href="#/home" class="mk-brand">
          <span class="mk-brand__dot"></span>
          <span>IndiEye</span>
        </a>
        <nav class="mk-nav">${nav}</nav>
        <button id="mk-theme-toggle" class="mk-btn mk-btn--ghost" type="button">${getMarketingTheme() === 'dark' ? 'Light' : 'Dark'} Theme</button>
      </header>
      <div class="mk-tape">${getMarketTapeHtml()}</div>

      <main class="mk-main">
        ${page.path === '/home' ? renderHero() : ''}

        <section class="mk-intro">
          <span class="${getBadgeClass(page.badge)}">${page.badge}</span>
          <h2>${page.title}</h2>
          <p>${page.summary}</p>
        </section>

        <section class="mk-grid">${sectionBlocks}</section>

        ${page.path === '/home' || page.path === '/mvp-demo' ? renderMediaBlock() : ''}
      </main>

      <footer class="mk-footer">
        <div class="mk-footer__leaders">
          <span>ADITYA — CTO, CEO</span>
          <span>PRITAM — CEO</span>
        </div>
        <div class="mk-footer__meta">
          <span class="mk-badge mk-badge--live">LIVE</span>
          <span class="mk-badge mk-badge--mvp">WORKING MVP</span>
          <span class="mk-badge mk-badge--build">IN ACTIVE BUILD</span>
          <span class="mk-badge mk-badge--roadmap">ROADMAP</span>
        </div>
      </footer>
    </div>
  `;
}

function getCurrentPath(): string {
  return window.location.hash.slice(1) || '/home';
}

type MarketingTheme = 'dark' | 'light';

function getMarketingTheme(): MarketingTheme {
  const raw = localStorage.getItem(MARKETING_THEME_KEY);
  return raw === 'light' ? 'light' : 'dark';
}

function setMarketingTheme(theme: MarketingTheme): void {
  localStorage.setItem(MARKETING_THEME_KEY, theme);
  const root = document.querySelector('.mk-site') as HTMLElement | null;
  if (root) {
    root.dataset.theme = theme;
    const btn = document.getElementById('mk-theme-toggle');
    if (btn) btn.textContent = theme === 'dark' ? 'Light Theme' : 'Dark Theme';
  }
}

function bindGlobeMotion(): void {
  const globe = document.getElementById('mk-globe') as HTMLElement | null;
  if (!globe) return;

  globe.addEventListener('pointermove', (event) => {
    const rect = globe.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) - 0.5;
    const y = ((event.clientY - rect.top) / rect.height) - 0.5;
    globe.style.setProperty('--mx', `${x * 18}deg`);
    globe.style.setProperty('--my', `${-y * 18}deg`);
  });

  globe.addEventListener('pointerleave', () => {
    globe.style.setProperty('--mx', '0deg');
    globe.style.setProperty('--my', '0deg');
  });
}

function bindThemeToggle(): void {
  const button = document.getElementById('mk-theme-toggle');
  if (!button) return;

  button.addEventListener('click', () => {
    const next = getMarketingTheme() === 'dark' ? 'light' : 'dark';
    setMarketingTheme(next);
  });
}

function mountCurrentPage(): void {
  const app = document.getElementById('app');
  if (!app) return;
  const page = getPage(getCurrentPath());
  app.innerHTML = renderPage(page);
  bindThemeToggle();
  bindGlobeMotion();
  document.title = `${page.label} — IndiEye`;
}

export function initMarketingSite(): void {
  if (!window.location.hash) {
    window.location.hash = '/home';
  }

  window.addEventListener('hashchange', mountCurrentPage);
  mountCurrentPage();
}
